import { SwitchButton } from '@components/core-ui/form-elements/buttons/Button/variations/SwitchButton';
import { TextButton } from '@components/core-ui/form-elements/buttons/Button/variations/TextButton';
import { Header } from '@components/Header';
import { HeaderSwitchWrapper } from '@components/Header/components/HeaderSwitchWrapper';
import { PagePagination } from '@components/core-ui/Pagination';
import { LinearProgressLoader } from '@components/core-ui/spinners/LinearProgressLoader';
import { downloadCSV } from '@helpers/downloadCSV';
import { useAPI } from '@providers/useAPI';
import { useAuthContext } from '@providers/AuthProvider';
import { routes } from '@routes';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { GetOrgMembersMemberResponseDto, OrganizationsService, PrismaOrganizationRoleDto, TeamsService } from 'app/API';
import { useDebouncedValue } from 'app/hooks/useDebouncedValue';
import { useListQueryParams } from 'app/hooks/useListQueryParams';
import { organizationsKeys, TMembersListParams } from 'app/queries/organizations/organizationsKeys';
import { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { createFetchOrgMembersQueryFn } from './api/fetchOrgMembers';
import { buildMembersCsvRows, MEMBERS_CSV_HEADERS } from './helpers/buildMembersCsvRows';
import { MainContent } from './styles/MainContent';
import { UsersTable } from './UsersTable';

const SEARCH_DEBOUNCE_MS = 300;

export const MembersPage = () => {
  const navigate = useNavigate();
  const { call } = useAPI();
  const queryClient = useQueryClient();
  const { organization } = useAuthContext();
  const organizationId = organization?.id ?? '';
  const organizationSlug = organization?.slug ?? '';

  const { page, pageSize, search, orderBy, orderType, setSearch, setPage, setPageSize, toggleSort } =
    useListQueryParams({
      defaults: { pageSize: 25, orderBy: 'createdAt', orderType: 'desc' },
    });
  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);
  const isSearchSettling = search !== debouncedSearch;
  const listParams = useMemo<TMembersListParams>(
    () => ({ page, pageSize, orderBy, orderType, search: debouncedSearch, teamsSlugFilter: '' }),
    [debouncedSearch, orderBy, orderType, page, pageSize],
  );

  const shouldShowReports = !!organization?.permissions?.viewReports;

  const membersQuery = useQuery({
    queryKey: organizationsKeys.membersList(organizationId, listParams),
    queryFn: createFetchOrgMembersQueryFn(call),
    placeholderData: keepPreviousData,
    enabled: organizationId !== '' && !isSearchSettling,
  });
  const members = membersQuery.data?.data ?? [];
  const membersCount = membersQuery.data?.count ?? 0;

  const invalidateMembers = () =>
    queryClient.invalidateQueries({ queryKey: organizationsKeys.membersByOrg(organizationId) });

  const toggleEnabledMutation = useMutation({
    mutationFn: (member: GetOrgMembersMemberResponseDto) =>
      call(() =>
        OrganizationsService.disableUser({
          userId: member.id,
          oid: organizationId,
          requestBody: { disable: !member.disabledInOrg },
        }),
      ),
    onError: () => toast.error('Failed to update the member'),
    onSettled: invalidateMembers,
  });

  const toggleCanManageLayoutsMutation = useMutation({
    mutationFn: (member: GetOrgMembersMemberResponseDto) =>
      call(() =>
        OrganizationsService.setMemberCanManageLayoutFlag({
          oid: organizationId,
          memberId: member.membershipId,
          requestBody: { value: !member.canManageLayouts },
        }),
      ),
    onError: () => toast.error('Failed to update the cloud layouts permission'),
    onSettled: invalidateMembers,
  });

  const removeTeamMutation = useMutation({
    mutationFn: ({ team, userId }: { team: PrismaOrganizationRoleDto; userId: string }) =>
      call(() => TeamsService.removeTeamFromUser({ oid: organizationId, userId, organizationRoleId: team.id })),
    onError: () => toast.error('Failed to remove the member from the team'),
    onSettled: invalidateMembers,
  });

  const exportMutation = useMutation({
    mutationFn: () =>
      call(() =>
        OrganizationsService.getOrgMembers({
          oid: organizationId,
          pageNo: '1',
          pageSize: String(Math.max(membersCount, 1)),
          orderBy: 'createdAt',
          orderType: 'desc',
          search: debouncedSearch,
          teamsSlugFilter: '',
        }),
      ),
    onSuccess: (response) => {
      downloadCSV(buildMembersCsvRows(response.data, organizationId), MEMBERS_CSV_HEADERS);
    },
    onError: () => toast.error('Failed to export members'),
  });

  const isMutating =
    toggleEnabledMutation.isPending || toggleCanManageLayoutsMutation.isPending || removeTeamMutation.isPending;
  const isLoading = membersQuery.isFetching || isSearchSettling || isMutating || exportMutation.isPending;
  const isExportDisabled =
    isSearchSettling || membersQuery.isPlaceholderData || !membersQuery.isSuccess || exportMutation.isPending;

  return (
    <>
      <Helmet>
        <title>Members - AutoScript</title>
      </Helmet>
      <Header
        leftSideContent={[
          <HeaderSwitchWrapper key="tabSwitcher">
            <SwitchButton
              active
              onClick={() => {
                navigate(routes.organizationMembers.make(organizationSlug));
              }}
            >
              Members
            </SwitchButton>
            <SwitchButton
              onClick={() => {
                navigate(routes.invitations.make(organizationSlug));
              }}
            >
              Invitations
            </SwitchButton>
            {shouldShowReports ? (
              <SwitchButton
                onClick={() => {
                  navigate(routes.organizationMemberReports.make(organizationSlug));
                }}
              >
                Reports
              </SwitchButton>
            ) : null}
            {shouldShowReports && organization?.featureFlags.ATTORNEY_VIEW_AVAILABLE ? (
              <SwitchButton
                onClick={() => {
                  navigate(routes.organizationAttorneysReport.make(organizationSlug));
                }}
              >
                Attorneys Report
              </SwitchButton>
            ) : null}
          </HeaderSwitchWrapper>,
        ]}
        title="Members"
        buttonLabel={members.length ? 'Export CSV' : undefined}
        buttonOnClick={members.length ? () => exportMutation.mutate() : undefined}
        buttonDisabled={isExportDisabled}
        setSearch={setSearch}
        search={search}
      />

      <LinearProgressLoader active={isLoading} />
      <MainContent>
        {membersQuery.isError ? (
          <div className="mb-4 flex items-center gap-3 text-xs text-[var(--color-text-muted)]">
            <span>Failed to load members.</span>
            <TextButton type="button" onClick={() => void membersQuery.refetch()}>
              Retry
            </TextButton>
          </div>
        ) : null}
        <UsersTable
          members={members}
          isMutating={isMutating}
          sortingField={orderBy}
          sortingReverse={orderType === 'desc'}
          toggleSort={toggleSort}
          onToggleEnabled={(member) => toggleEnabledMutation.mutate(member)}
          onToggleCanManageLayouts={(member) => toggleCanManageLayoutsMutation.mutate(member)}
          onRemoveTeam={(team, userId) => removeTeamMutation.mutate({ team, userId })}
          reload={() => void invalidateMembers()}
        />
        <PagePagination
          totalItemsCount={membersCount}
          page={page}
          onPageChange={setPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
        />
      </MainContent>
    </>
  );
};
